import { useCallback, useMemo, useState } from 'react'

import { getJSON, setJSON } from '#/shared/lib/storage'

import type { DataTableColumn, DataTablePin } from './data-table-types'

type PinOverride = DataTablePin | null
type PinOverrides = Record<string, PinOverride>

function readFromStorage(storageKey: string | undefined): PinOverrides {
  if (!storageKey) return {}
  const parsed = getJSON<PinOverrides>(storageKey)
  if (parsed && typeof parsed === 'object') return parsed
  return {}
}

function writeToStorage(
  storageKey: string | undefined,
  overrides: PinOverrides,
) {
  if (!storageKey) return
  setJSON(storageKey, overrides)
}

export function useColumnPins<T>({
  columns,
  storageKey,
}: {
  columns: DataTableColumn<T>[]
  storageKey?: string
}) {
  const [overrides, setOverrides] = useState<PinOverrides>(() =>
    readFromStorage(storageKey),
  )

  const setPin = useCallback(
    (key: string, pin: PinOverride) => {
      setOverrides((prev) => {
        const next: PinOverrides = { ...prev, [key]: pin }
        writeToStorage(storageKey, next)
        return next
      })
    },
    [storageKey],
  )

  const effectivePins = useMemo(() => {
    const map = new Map<string, DataTablePin>()
    for (const col of columns) {
      if (Object.hasOwn(overrides, col.key)) {
        const override = overrides[col.key]
        if (override) map.set(col.key, override)
      } else if (col.pinned) {
        map.set(col.key, col.pinned)
      }
    }
    return map
  }, [columns, overrides])

  return { effectivePins, setPin }
}
